// Lib
import { get, map, uniq, isEmpty, reduce as reduceFp, forEach as forEachFp } from 'lodash/fp';

// Utils
import { chooseSingular, listSeparator, pluralise } from '../utils/stringUtil';
import { getElementType } from '../elements/utils/elementPropertyUtils';
import { getElementDisplayName } from '../elements/elementRegistry';
import { getElementTypeTranslationKey } from '../utils/i18n/coreTranslationHelpers';

// Constants
import { BOARD_DEFAULT_TITLE } from '../boards/boardConstants';
import { isBoard, isSketch, isTask } from '../elements/utils/elementTypeUtils';
import { ElementChangeType } from './notificationConstants';
import { ElementType } from '../elements/elementTypes';

const CHANGE_TYPE_SORT_ORDER = ['Added', 'Updated', 'Completed'];
const ELEMENT_TYPE_SORT_ORDER = [
    ElementType.BOARD_TYPE,
    ElementType.CARD_TYPE,
    ElementType.IMAGE_TYPE,
    ElementType.DOCUMENT_TYPE,
    ElementType.FILE_TYPE,
    ElementType.LINK_TYPE,
    ElementType.TASK_LIST_TYPE,
    ElementType.TASK_TYPE,
    ElementType.LINE_TYPE,
    ElementType.COLUMN_TYPE,
    ElementType.COMMENT_THREAD_TYPE,
];

const reduce = reduceFp.convert({ cap: false });
const forEach = forEachFp.convert({ cap: false });

const getPluralSuffix = (count) => (count === 1 ? '_one' : '_other');

const getKeyWithoutNamespace = (key) => {
    if (!key.includes(':')) return key;
    return key.split(':').slice(1).join(':');
};

const getTranslatedValue = (notificationsTranslate, key, context = undefined) => {
    if (!notificationsTranslate) return undefined;

    const translated = notificationsTranslate(key, context);

    if (!translated) return undefined;
    if (translated === key) return undefined;

    const keyWithoutNamespace = getKeyWithoutNamespace(key);
    if (translated === keyWithoutNamespace) return undefined;

    return translated;
};

const getPluralTranslatedValue = (notificationsTranslate, keyBase, count) => {
    const context = { count };
    const countBasedText = getTranslatedValue(notificationsTranslate, keyBase, context);
    const suffix = getPluralSuffix(count);
    const explicitPluralKey = `${keyBase}${suffix}`;
    const explicitPluralText = getTranslatedValue(notificationsTranslate, explicitPluralKey, context);

    if (!countBasedText) return explicitPluralText;
    if (!explicitPluralText) return countBasedText;

    return explicitPluralText !== countBasedText ? explicitPluralText : countBasedText;
};

const getElementTypeTranslationKeyBase = (elementType) => {
    if (isSketch(elementType)) return 'elements:elementTypes.sketch.sketch';
    if (isTask(elementType)) return 'elements:elementTypes.taskItem.taskItem';

    const translationKey = getElementTypeTranslationKey(elementType);
    return `elements:elementTypes.${translationKey}.${translationKey}`;
};

const getElementTypeFallbackText = (elementType, count) => {
    if (isSketch(elementType)) {
        return chooseSingular(count, 'sketch', 'sketches');
    }

    const fallbackText = getElementDisplayName(elementType).toLowerCase();

    if (isTask(elementType)) {
        return chooseSingular(count, `${fallbackText} item`, `${fallbackText} items`);
    }

    return pluralise(fallbackText, count);
};

const getChangeTypeText = (notificationsTranslate, changeType, actorCount) => {
    const changeTypeKey = `changeTypes.${changeType.toLowerCase()}`;

    const actorCountChangeTypeText = getPluralTranslatedValue(notificationsTranslate, changeTypeKey, actorCount);
    if (actorCountChangeTypeText) return actorCountChangeTypeText;

    const fallbackChangeTypeText = getTranslatedValue(notificationsTranslate, changeTypeKey);
    return fallbackChangeTypeText || changeType.toLowerCase();
};

const updateSummarySortFn = (summaryA, summaryB) => {
    const changeTypeIndexA = CHANGE_TYPE_SORT_ORDER.indexOf(summaryA.changeType);
    const changeTypeIndexB = CHANGE_TYPE_SORT_ORDER.indexOf(summaryB.changeType);

    if (changeTypeIndexA !== changeTypeIndexB) return changeTypeIndexA - changeTypeIndexB;

    const elementTypeIndexA = ELEMENT_TYPE_SORT_ORDER.indexOf(summaryA.elementType);
    const elementTypeIndexB = ELEMENT_TYPE_SORT_ORDER.indexOf(summaryB.elementType);

    return elementTypeIndexA - elementTypeIndexB;
};

const changesValid = (changes) => {
    if (!changes) {
        console.warn('No changes to check');
        return false;
    }

    return true;
};

const isTaskCompletionChange = ({ elementType, changes }) => {
    if (!changesValid(changes)) return false;
    return isTask(elementType) && changes.some((change) => change.changeType === ElementChangeType.COMPLETE);
};

export const getChangeType = ({ elementType, changes }) => {
    if (!changesValid(changes)) return null;

    if (isTask(elementType) && changes.every((change) => change.changeType === ElementChangeType.COMPLETE)) {
        return null;
    }

    return changes.some(
        (change) => change.changeType === ElementChangeType.CREATE || change.changeType === ElementChangeType.MOVE_IN,
    )
        ? 'Added'
        : 'Updated';
};

const isNewBoardChangeWithoutCustomTitle = (changeEntry) =>
    changeEntry &&
    changeEntry.isNewBoard &&
    (isEmpty(changeEntry.boardTitle) || changeEntry.boardTitle.toLowerCase() === BOARD_DEFAULT_TITLE.toLowerCase());

export const getUpdateSummaryList = (elementChangesMap) => {
    const changeCountMap = reduce(
        (acc, changeEntry) => {
            const { elementType, changes } = changeEntry;

            // Don't include new board changes that don't have custom titles
            if (isNewBoardChangeWithoutCustomTitle(changeEntry)) return acc;

            acc[elementType] = acc[elementType] || {};

            const changeType = getChangeType({ elementType, changes });

            if (changeType) {
                acc[elementType][changeType] = (acc[elementType][changeType] || 0) + 1;
            }

            if (isTaskCompletionChange({ elementType, changes })) {
                acc[elementType].Completed = (acc[elementType].Completed || 0) + 1;
            }

            return acc;
        },
        {},
        elementChangesMap,
    );

    return reduce(
        (descriptionList, elementTypeChangeCounts, elementType) => {
            forEach((count, changeType) => {
                if (!count) return;

                descriptionList.push({
                    elementType,
                    groupName: elementType.toLowerCase(),
                    changeType,
                    count,
                });
            }, elementTypeChangeCounts);

            return descriptionList;
        },
        [],
        changeCountMap,
    ).sort(updateSummarySortFn);
};

export const getNewBoardTitlesWithBoardIds = (elementChanges) =>
    reduce(
        (newBoardTitles, changeEntry) => {
            const { _id, elementType, isNewBoard, boardTitle } = changeEntry;

            if (isBoard(elementType) && isNewBoard && !isNewBoardChangeWithoutCustomTitle(changeEntry)) {
                newBoardTitles.push({ boardId: _id, title: boardTitle });
            }

            return newBoardTitles;
        },
        [],
        elementChanges,
    );

export const getNewBoardTitles = (elementChanges) => getNewBoardTitlesWithBoardIds(elementChanges).map(get('title'));

export const getElementChangeElementTypes = (elementChanges) => {
    const elementTypes = map(getElementType, elementChanges);
    return uniq(elementTypes);
};

export const getElementChangeElementTypeDisplayName = ({ elementType, count }, notificationsTranslate) => {
    const translationKeyBase = getElementTypeTranslationKeyBase(elementType);
    const translatedElementTypeText = getPluralTranslatedValue(notificationsTranslate, translationKeyBase, count);

    if (translatedElementTypeText) return translatedElementTypeText;
    return getElementTypeFallbackText(elementType, count);
};

const defaultNotificationsTranslate = () => undefined;

export const describeChanges = (
    elementChanges,
    notificationsTranslate = defaultNotificationsTranslate,
    actorCount = 1,
) => {
    if (isEmpty(elementChanges)) {
        const conjunction = notificationsTranslate('conjunctions.in') || 'in';
        return { conjunction };
    }

    const updateSummaryList = getUpdateSummaryList(elementChanges);

    const isSingleAddedChange = updateSummaryList.length === 1 && updateSummaryList[0].changeType === 'Added';
    const conjunctionKey = isSingleAddedChange ? 'conjunctions.to' : 'conjunctions.in';
    const conjunctionDefault = isSingleAddedChange ? 'to' : 'in';
    const conjunction = getTranslatedValue(notificationsTranslate, conjunctionKey) || conjunctionDefault;
    const finalConjunction = getTranslatedValue(notificationsTranslate, 'conjunctions.and') || 'and';

    const sentence = updateSummaryList
        .map(({ count, changeType, elementType }, index) => {
            const separator = listSeparator(index, updateSummaryList.length, finalConjunction);
            const showElementType = index === 0;
            const elementTypeText = showElementType
                ? ` ${getElementChangeElementTypeDisplayName({ elementType, count }, notificationsTranslate)}`
                : '';
            const changeTypeText = getChangeTypeText(notificationsTranslate, changeType, actorCount);

            return `${changeTypeText} ${count}${elementTypeText}${separator}`;
        })
        .join('');

    return {
        sentence,
        conjunction,
    };
};
